import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/* DocumentHead — loads the full SEO list once, then updates document.title +
   meta[name=description] + meta[name=robots] on every public route change.

   Backed by GET /api/page-seo (merged defaults + admin overrides). */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let cache = null;       // [{ path, title, description, robots }]
let cachePromise = null;

const loadOnce = () => {
  if (cache) return Promise.resolve(cache);
  if (cachePromise) return cachePromise;
  cachePromise = fetch(`${API}/page-seo`)
    .then((r) => (r.ok ? r.json() : []))
    .then((d) => { cache = Array.isArray(d) ? d : []; return cache; })
    .catch(() => { cache = []; return cache; });
  return cachePromise;
};

const setMeta = (name, value) => {
  if (!value) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const setOg = (prop, value) => {
  if (!value) return;
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

export const DocumentHead = () => {
  const { pathname } = useLocation();
  const [, force] = useState(0);

  useEffect(() => {
    let cancelled = false;
    loadOnce().then(() => !cancelled && force((n) => n + 1));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Don't touch document head on admin routes — AccessShell owns those.
    if (pathname.startsWith("/access")) return;
    const list = cache || [];
    // exact match first; otherwise look for prefix on dynamic paths (/integrations/jane-app etc.)
    const exact = list.find((p) => p.path === pathname);
    const prefix = !exact && list.find((p) => pathname.startsWith(p.path) && p.path !== "/");
    const meta = exact || prefix;
    if (!meta) return;
    document.title = meta.title || document.title;
    setMeta("description", meta.description);
    setMeta("robots", meta.robots || "index, follow");
    setOg("og:title", meta.title);
    setOg("og:description", meta.description);
    setOg("og:url", typeof window !== "undefined" ? window.location.href : "");
  }, [pathname, cache]);

  return null;
};
